"use client";

import { makeFunctionReference } from "convex/server";
import { useQuery } from "convex/react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { QuerySkeleton } from "@/components/ui/skeleton";

interface TrackingSnapshot {
  link: { token: string; expiresAt?: string };
  load: {
    reference: string;
    lifecycleState: string;
    origin: { label: string; city: string; state: string };
    destination: { label: string; city: string; state: string };
    latestEtaIso?: string;
    redactions: string[];
  };
}

const getPublicTracking = makeFunctionReference<
  "query",
  { tenantId: string; token: string },
  TrackingSnapshot | null
>("trackingLinks:getPublic");

export function TrackingLinkView({ tenantId, token }: { tenantId: string; token: string }) {
  if (!process.env.NEXT_PUBLIC_CONVEX_URL) {
    return <TrackingPanel snapshot={demoSnapshot(token)} />;
  }
  return <LiveTrackingLinkView tenantId={tenantId} token={token} />;
}

function LiveTrackingLinkView({ tenantId, token }: { tenantId: string; token: string }) {
  const snapshot = useQuery(getPublicTracking, { tenantId, token });
  return <TrackingPanel snapshot={snapshot} />;
}

function TrackingPanel({ snapshot }: { snapshot: TrackingSnapshot | null | undefined }) {
  if (snapshot === undefined) {
    return (
      <main className="mx-auto max-w-3xl px-6 py-8">
        <QuerySkeleton surface="trackingLink" />
      </main>
    );
  }
  if (snapshot === null) {
    return <div className="mx-auto max-w-3xl px-6 py-8 text-sm text-muted-foreground">Tracking link unavailable.</div>;
  }

  return (
    <main className="mx-auto max-w-3xl space-y-4 px-6 py-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{snapshot.load.reference}</h1>
          <p className="mt-1 text-sm text-muted-foreground">Shipment tracking</p>
        </div>
        <Badge variant="info">{snapshot.load.lifecycleState.replaceAll("_", " ")}</Badge>
      </div>

      <Card>
        <CardHeader className="p-3">
          <CardTitle className="text-sm">Route</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 p-3 sm:grid-cols-2">
          <Stop label="Pickup" stop={snapshot.load.origin} />
          <Stop label="Delivery" stop={snapshot.load.destination} />
        </CardContent>
      </Card>

      <Card>
        <CardContent className="grid gap-3 p-3 text-sm sm:grid-cols-2">
          <div>
            <div className="text-xs uppercase text-muted-foreground">Latest ETA</div>
            <div className="mt-1 font-medium">{snapshot.load.latestEtaIso ?? "Pending"}</div>
          </div>
          <div>
            <div className="text-xs uppercase text-muted-foreground">Visibility</div>
            <div className="mt-1 font-medium">
              {snapshot.load.redactions.length > 0 ? "Blind-aware public view" : "Standard public view"}
            </div>
          </div>
        </CardContent>
      </Card>
    </main>
  );
}

function Stop({
  label,
  stop,
}: {
  label: string;
  stop: { label: string; city: string; state: string };
}) {
  return (
    <div className="rounded-md border border-border p-3 text-sm">
      <div className="text-xs uppercase text-muted-foreground">{label}</div>
      <div className="mt-1 font-medium">{stop.label}</div>
      <div className="text-muted-foreground">
        {stop.city}, {stop.state}
      </div>
    </div>
  );
}

function demoSnapshot(token: string): TrackingSnapshot {
  return {
    link: { token },
    load: {
      reference: "HB-2001",
      lifecycleState: "in_transit",
      origin: { label: "Pickup facility withheld", city: "Dallas", state: "TX" },
      destination: { label: "Delivery facility withheld", city: "Atlanta", state: "GA" },
      latestEtaIso: "2026-05-06T16:45:00.000Z",
      redactions: ["shipper", "consignee"],
    },
  };
}
